import axios from 'axios';
import api from '@/utils/api';
import {createImageLoadingQueue} from './imageLoadingQueue';

export function createGalleryImageLoader() {
    const queue = createImageLoadingQueue();
    const entries = new Map();
    const update = entry => entry.job.setPriority(entry.visible ? 'visible' : entry.near ? 'near' : 'hidden');
    const observe = (field, options) => typeof IntersectionObserver === 'undefined' ? null
        : new IntersectionObserver(changes => changes.forEach(change => {
            const entry = entries.get(change.target);
            if (!entry) return;
            entry[field] = change.isIntersecting && change.intersectionRect.width > 0 && change.intersectionRect.height > 0;
            update(entry);
        }), options);
    const visible = observe('visible', {threshold: 0});
    const near = observe('near', {rootMargin: '200px 0px', scrollMargin: '200px 0px', threshold: 0});

    return {
        pauseBackground: queue.pauseBackground,
        register(element, url, onLoad, onError) {
            const job = queue.register({
                load() {
                    const source = axios.CancelToken.source();
                    return {
                        promise: api.get(url, {responseType: 'blob', cancelToken: source.token, ignoreModal: true, localErrorHandling: true})
                            .then(response => response.data),
                        cancel: () => source.cancel('Gallery image is no longer needed')
                    };
                },
                onLoad,
                onError
            });
            const entry = {job, visible: false, near: false};
            entries.set(element, entry);
            if (visible && near) {
                visible.observe(element);
                near.observe(element);
            } else job.setPriority('visible');
            return {
                retry: job.retry,
                dispose() {
                    if (visible) visible.unobserve(element);
                    if (near) near.unobserve(element);
                    entries.delete(element);
                    job.dispose();
                }
            };
        },
        destroy() {
            if (visible) visible.disconnect();
            if (near) near.disconnect();
            entries.clear();
            queue.destroy();
        }
    };
}
